import { cn } from '@latitude/token-utils';
import { useActionListContext } from 'ActionListContext';
import type { MouseEvent } from 'react';
import { useCallback, useMemo } from 'react';
import type { OptionComponentProps } from 'types';

import { ActionListCheckbox } from './ActionListCheckbox';
import { DefaultLabelComponent } from './DefaultLabelComponent';

const iconClassName = '[&>*]:size-sizing-comp-action-list-icon';

export const DefaultOptionComponent = <T = undefined,>({
  isSelected,
  item,
  showSelectionElement,
  isTruncated = false,
}: OptionComponentProps<T>) => {
  const { isMulti, selectionElementPosition: passedPosition } =
    useActionListContext();

  const selectionElementPosition = useMemo(() => {
    if (passedPosition) {
      return passedPosition;
    }

    if (item.leadingIcon) {
      return 'right';
    }

    return 'left';
  }, [item, passedPosition]);

  const leadingElementClasses = cn([
    'flex items-center gap-2',
    {
      // 38px is the padding on the label + the padding on the popover
      'w-[calc(var(--radix-popper-anchor-width)-38px)]': isTruncated,
      'w-full': !isTruncated,
    },
  ]);

  const renderLeadingCheckbox =
    showSelectionElement && isMulti && selectionElementPosition === 'left';

  const leadingContent = (
    <>
      {item.leadingIcon ? (
        <span className={iconClassName}>{item.leadingIcon}</span>
      ) : null}
      <DefaultLabelComponent
        label={item.label}
        labelClassName={item.labelClassName}
        subtext={item.subtext}
        disabled={item.disabled}
        selected={isSelected}
        truncated={isTruncated}
      />
    </>
  );

  const leadingElement = (
    <div
      key={`leading-${item.value}`}
      data-testid="action-list-leading-element"
      className={leadingElementClasses}
    >
      {renderLeadingCheckbox ? (
        <ActionListCheckbox
          isSelected={isSelected}
          canSelect={!item.disabled}
          className="justify-self-end"
        >
          {leadingContent}
        </ActionListCheckbox>
      ) : (
        leadingContent
      )}
    </div>
  );

  const renderTrailingCheckbox =
    showSelectionElement && isMulti && selectionElementPosition === 'right';

  const trailingContent = (
    <>
      {item.trailingIcon ? (
        <span className={iconClassName}>{item.trailingIcon}</span>
      ) : null}
      {item.trailingElement ? <span>{item.trailingElement}</span> : null}
    </>
  );

  const trailingElement = (
    <div
      key={`trailing-${item.value}`}
      data-testid="action-list-trailing-element"
      className="flex items-center gap-2 justify-self-end"
    >
      {renderTrailingCheckbox ? (
        <ActionListCheckbox
          isSelected={isSelected}
          canSelect={!item.disabled}
          className="justify-self-end"
          childrenBefore
        >
          {trailingContent}
        </ActionListCheckbox>
      ) : (
        trailingContent
      )}
    </div>
  );

  const handleLinkClick = useCallback((e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey) {
      e.stopPropagation();
      return;
    }
    e.preventDefault();
  }, []);

  if (item.href) {
    return (
      <a
        href={item.href}
        className="flex w-full items-center justify-between text-inherit no-underline"
        onClick={handleLinkClick}
      >
        {leadingElement}
        {trailingElement}
      </a>
    );
  }

  return (
    <div
      className="flex w-full items-center justify-between"
      onClick={item.disabled ? undefined : item.onClick}
    >
      {leadingElement}
      {trailingElement}
    </div>
  );
};
